import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { Modal, Button, Select, Input, InputNumber, Tag, Spinner, EmptyState, message, Tooltip } from '../../ui';
import { Plus, Trash2, Info, Send, Bike, Car, Bus, Plane, TrainFront, CarTaxiFront, Route, Pencil, Check, X } from 'lucide-react';
import dayjs from 'dayjs';
import { request } from '../../api/client';
import { FileUploadField } from '../../components/FileUploadField';
import { AttachmentCell } from '../../components/AttachmentPreview';
import { inr2 } from '../../lib/format';
import { proofRule } from '../../lib/expenseProof';
import { formatIst } from '../../lib/time';
import { ShiftChip } from '../../components/VisitShiftGroups';

const OTHER_CATEGORIES = ['FOOD', 'HOTEL', 'LOCAL_CONVEYANCE', 'TOLL_PARKING', 'OTHER'];
const DESC_REQUIRED = new Set(['HOTEL', 'OTHER']);

/* The words a rep reads for a stored category code. The CODE is what is saved and what the server's
   proof rules key on; only the display changes. "TOLL PARKING" and "LOCAL CONVEYANCE" were the raw codes. */
const CATEGORY_LABEL: Record<string, string> = {
  FOOD: 'Food',
  HOTEL: 'Hotel',
  LOCAL_CONVEYANCE: 'Local conveyance',
  TOLL_PARKING: 'Toll & parking',
  OTHER: 'Other',
};
/** A code outside the list (a travel mode, the allowance) reads in sentence case rather than capitals. */
const categoryLabel = (code?: string | null): string => {
  const c = String(code ?? '');
  if (CATEGORY_LABEL[c]) return CATEGORY_LABEL[c];
  const words = c.replace(/_/g, ' ').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

type Draft = {
  key: string;
  kind: 'TRAVEL' | 'OTHER';
  travelMode?: string;
  distanceKm?: number;
  category?: string;
  amount?: number;
  description?: string;
  visitId?: string;
  /** The shift (attendance session) the cost belongs to; unset = the whole day. */
  sessionId?: string;
  billUrl?: string;
};
const blank = (kind: Draft['kind']): Draft => ({ key: Math.random().toString(36).slice(2), kind });

/** One of the day's shifts, as the server numbers them (the same numbering as My Field Day). */
type DayShift = { id: string; no: number; checkInAt: string; checkOutAt: string | null };
/** One of the day's visits, placed in its shift by the server. */
type DayVisit = { id: string; label: string; shiftNo: number | null; sessionId: string | null };

const shiftText = (s: DayShift) =>
  `Shift ${s.no} · ${formatIst(s.checkInAt, 'h:mm A')} – ${s.checkOutAt ? formatIst(s.checkOutAt, 'h:mm A') : 'on duty'}`;

/**
 * WHERE IN THE DAY A COST BELONGS: one of the day's visits, one of its shifts, or the whole day.
 *
 * A day with several shifts (punched out for lunch, back in for the afternoon) could only tag a cost
 * to a visit or to "the whole day", so the afternoon's fuel could not be lined up against the
 * afternoon's route. The shift is asked only when there IS more than one — on a one-shift day every
 * cost is in that shift and the question has no answer to give. A visit decides its own shift (it sits
 * inside one), so choosing a visit fills the shift in and locks it; the server refuses a mismatch too.
 */
function DayLinkFields({ visits, shifts, visitId, sessionId, onChange }: {
  visits: DayVisit[];
  shifts: DayShift[];
  visitId?: string;
  sessionId?: string;
  onChange: (next: { visitId?: string; sessionId?: string }) => void;
}) {
  const multiShift = shifts.length > 1;
  const visit = visits.find((v) => v.id === visitId);
  const shownShift = visit ? (visit.sessionId ?? undefined) : sessionId;
  return (
    <>
      {visits.length > 0 && (
        <label className="min-w-48 flex-1">
          <span className="mb-1 block text-xs text-muted">For a particular visit (optional)</span>
          <Select className="w-full" allowClear placeholder={multiShift ? 'Not for one visit' : 'A cost for the whole day'}
            value={visitId}
            onChange={(v) => {
              const picked = visits.find((x) => x.id === v);
              onChange({ visitId: v || undefined, sessionId: picked ? (picked.sessionId ?? undefined) : sessionId });
            }}
            options={visits.map((v) => ({
              value: v.id,
              label: multiShift ? `${v.label} — ${v.shiftNo ? `Shift ${v.shiftNo}` : 'between shifts'}` : v.label,
            }))} />
        </label>
      )}
      {multiShift && (
        <label className="min-w-48 flex-1">
          <span className="mb-1 block text-xs text-muted">
            Which shift {visit ? '(follows the visit)' : '(optional)'}
          </span>
          <Select className="w-full" allowClear={!visit} disabled={!!visit} placeholder={visit ? 'Between shifts' : 'The whole day'}
            value={shownShift}
            onChange={(v) => onChange({ visitId, sessionId: v || undefined })}
            options={shifts.map((x) => ({ value: x.id, label: shiftText(x) }))} />
        </label>
      )}
    </>
  );
}

/**
 * The rep's expense claim for ONE FIELD DAY.
 *
 * A day's costs belong to the day, not to a single visit — the day's fuel can't be split across its
 * farmers, and the daily allowance is decided by the whole day's distance. So this is where a rep
 * builds up the day: travel by the kilometre (priced by the server from the rate card), anything
 * billed at actuals, each line optionally tagged to one of that day's visits.
 *
 * The allowance is NOT entered here. It is worked out on submit, once the day's distance is final —
 * what's shown before then is a preview of what the current distance would earn.
 */
/**
 * A FACE PER TRAVEL MODE.
 *
 * Seven near-identical lines of text ("Bus — at actuals", "Train — at actuals" …) are read one word
 * at a time; a shape is recognised before it is read, which is what a rep picking a mode at the end of
 * a long day actually needs. The colours are the transport's own — they carry no status meaning, so
 * they are kept muted enough not to compete with the amber/red the rest of this form uses for money
 * and for errors.
 */
const TRAVEL_ICON: Record<string, { icon: JSX.Element; tint: string }> = {
  BIKE: { icon: <Bike className="size-4" />, tint: 'text-emerald-600 dark:text-emerald-400' },
  CAR: { icon: <Car className="size-4" />, tint: 'text-blue-600 dark:text-blue-400' },
  BUS: { icon: <Bus className="size-4" />, tint: 'text-amber-600 dark:text-amber-400' },
  FLIGHT: { icon: <Plane className="size-4" />, tint: 'text-sky-600 dark:text-sky-400' },
  TRAIN: { icon: <TrainFront className="size-4" />, tint: 'text-violet-600 dark:text-violet-400' },
  TAXI: { icon: <CarTaxiFront className="size-4" />, tint: 'text-yellow-600 dark:text-yellow-500' },
  OTHER: { icon: <Route className="size-4" />, tint: 'text-muted' },
};

export function DayExpenseModal({ date, visits = [], onClose, onSaved }: {
  date: string;
  /** That day's visits, offered as an optional tag on a line. */
  visits?: Array<{ id: string | number; label: string }>;
  onClose: () => void;
  onSaved?: () => void;
}) {
  const qc = useQueryClient();
  const [drafts, setDrafts] = useState<Draft[]>([blank('TRAVEL')]);

  const { data: claim, isLoading } = useQuery({
    queryKey: ['day-claim', date],
    queryFn: () => request<any>({ url: `/expense-reports/day/${date}` }),
  });
  const { data: policy } = useQuery({
    queryKey: ['expense-policy', { on: date }],
    queryFn: () => request<any>({ url: '/expense-policy', params: { on: date } }),
  });

  const report = claim?.report ?? null;
  /* The day's shifts and visits come from the SERVER, placed in their shift by the same windows the
     Visits list uses. Two of the three places this sheet opens from passed no visits at all, so a rep
     filing from the Expenses page could not tag a cost to a visit; the prop is now only a fallback. */
  const shifts: DayShift[] = claim?.shifts ?? report?.dayShifts ?? [];
  const dayVisits: DayVisit[] = (claim?.visits ?? report?.dayVisitList ?? []).length
    ? (claim?.visits ?? report?.dayVisitList)
    : visits.map((v) => ({ id: String(v.id), label: v.label, shiftNo: null, sessionId: null }));
  const multiShift = shifts.length > 1;
  const locked = !!report && !['DRAFT', 'CORRECTION_REQUIRED'].includes(report.status);
  const rates: any[] = policy?.travelRates ?? [];
  const rateOf = (code?: string) => rates.find((r) => r.code === code);
  /** Proof for a non-travel cost: the policy row when the category is also a travel code (OTHER, TAXI…),
   *  otherwise a receipt — mirrors the server's proofRules, which refuses to submit without it. */
  const otherProof = (category?: string) => proofRule(category, rates);

  const patch = (key: string, p: Partial<Draft>) => setDrafts((a) => a.map((d) => (d.key === key ? { ...d, ...p } : d)));
  const drop = (key: string) => setDrafts((a) => a.filter((d) => d.key !== key));

  // What the lines already saved on the claim add up to, and how far the day has travelled so far.
  const saved = useMemo(() => {
    const items: any[] = report?.items ?? [];
    return {
      total: items.reduce((s, i) => s + Number(i.amount || 0), 0),
      km: items.reduce((s, i) => s + Number(i.distanceKm || 0), 0),
      items,
    };
  }, [report]);

  // Distance and value the drafts would add, so the rep sees where the day lands before saving.
  const pending = useMemo(() => {
    let total = 0; let km = 0;
    for (const d of drafts) {
      if (d.kind === 'TRAVEL') {
        const r = rateOf(d.travelMode);
        if (r?.basis === 'PER_KM' && d.distanceKm) { km += Number(d.distanceKm); total += Number(d.distanceKm) * Number(r.ratePerKm); }
        else if (r?.basis === 'ACTUAL' && d.amount) total += Number(d.amount);
      } else if (d.amount) total += Number(d.amount);
    }
    return { total, km };
  }, [drafts, rates]);

  /* A DAY MAY CARRY SEVERAL CLAIMS. `report` is the one still taking lines (null once all are sent —
     the next line opens a new claim); `others` are the day's claims already sent. The footer and the
     allowance preview speak for the DAY, the way the server computes the allowance. */
  const others: any[] = (claim?.others ?? []).filter((o: any) => o.status !== 'REJECTED');
  const othersTotal = others.reduce((s, o) => s + Number(o.total || 0), 0);
  const othersKm = others.reduce((s, o) => s + Number(o.distanceKm || 0), 0);
  const othersDa = others.reduce((s, o) => s + Number(o.daAmount || 0), 0);
  const dayKm = saved.km + pending.km + othersKm;
  // Preview only — the server recomputes this at submit from the day's final distance, less any
  // allowance the day's earlier claims already carry.
  const daPreview = useMemo(() => {
    const slab = (policy?.daSlabs ?? []).find((s: any) => dayKm >= s.minKm);
    return Math.max(0, (slab ? Number(slab.amount) : 0) - othersDa);
  }, [policy, dayKm, othersDa]);

  /*
   * NO SEPARATE "SAVE" STEP. The sheet used to carry an "Add to claim" button between Close and Submit
   * day, and it was a trap both ways: Submit day sent only lines already saved, so a line typed and
   * submitted was silently left out; and Close threw typed lines away without a word. Now the lines on
   * the form are saved by whichever way the rep leaves — Close keeps them as the day's draft, Submit day
   * saves them and sends the day — and an incomplete line stops either, naming what it still needs.
   */
  const isTouched = (d: Draft) => !!(d.travelMode || d.category || d.amount != null || d.distanceKm != null
    || (d.description ?? '').trim() || d.billUrl || d.visitId || d.sessionId);
  const hasTyped = drafts.some(isTouched);

  /** Saves the typed lines to the day's claim. Returns false when there was nothing to save. */
  const saveTyped = async (): Promise<boolean> => {
    /*
     * An UNTOUCHED row is the empty form, not an error.
     *
     * The sheet always opens with a blank TRAVEL row. A rep who clicks "Other cost", fills a hotel
     * bill and saves was told "Pick how you travelled" — about the blank row they never chose to add
     * — and no request was sent. Nothing on screen said which row was at fault, and the only way out
     * was to notice the little bin icon on the row above. So blank rows are dropped here, and the
     * validation below applies to rows the rep actually filled in.
     */
    const touched = drafts.filter(isTouched);
    if (!touched.length) return false;
    const lines = touched.map((d) => {
      if (d.kind === 'TRAVEL') {
        const r = rateOf(d.travelMode);
        if (!r) throw new Error('Pick how you travelled');
        if (r.basis === 'PER_KM') {
          if (!d.distanceKm || d.distanceKm <= 0) throw new Error(`Enter the kilometres travelled by ${r.label}`);
          // Proof is enforced at submit and approval too (server) — the policy decides which modes need it.
          if (r.proofRequired && !d.billUrl) throw new Error(`${r.label} needs ${r.proofLabel ?? 'proof'} attached`);
          return { kind: 'TRAVEL_KM', travelMode: r.code, distanceKm: Number(d.distanceKm), visitId: d.visitId, sessionId: d.sessionId, billUrl: d.billUrl, description: d.description };
        }
        if (!d.amount || d.amount <= 0) throw new Error(`Enter what you spent on ${r.label}`);
        if (r.proofRequired && !d.billUrl) throw new Error(`${r.label} needs ${r.proofLabel ?? 'proof'} attached`);
        return { kind: 'ACTUAL', category: r.code, amount: Number(d.amount), visitId: d.visitId, sessionId: d.sessionId, billUrl: d.billUrl, description: d.description };
      }
      if (!d.category) throw new Error('Pick what the expense was for');
      if (!d.amount || d.amount <= 0) throw new Error('Every expense needs an amount');
      if (DESC_REQUIRED.has(d.category) && !d.description?.trim()) throw new Error(`${categoryLabel(d.category)} expenses need a description`);
      // Every cost the rep paid needs its receipt — the same rule the server applies at submit.
      if (otherProof(d.category).required && !d.billUrl) throw new Error(`${categoryLabel(d.category)} needs a ${otherProof(d.category).label.toLowerCase()} attached`);
      return { kind: 'ACTUAL', category: d.category, amount: Number(d.amount), description: d.description, visitId: d.visitId, sessionId: d.sessionId, billUrl: d.billUrl };
    });
    await request({ url: '/expense-reports/day', method: 'POST', data: { expenseDate: date, lines } });
    setDrafts([blank('TRAVEL')]);
    qc.invalidateQueries({ queryKey: ['day-claim', date] });
    qc.invalidateQueries({ queryKey: ['expense-reports'] });
    qc.invalidateQueries({ queryKey: ['my-day'] });
    onSaved?.();
    return true;
  };
  const errText = (e: any, fallback: string) => e?.response?.data?.error?.message ?? e?.message ?? fallback;

  /** Close keeps what was typed: saved as the day's draft, then the sheet closes. */
  const closeKeeping = useMutation({
    mutationFn: saveTyped,
    onSuccess: (didSave) => {
      if (didSave) message.success('Saved to the day’s draft — submit the day when you’re done');
      onClose();
    },
    onError: (e: any) => message.error(`${errText(e, 'Could not save')} — or remove that line to close without it`),
  });
  const requestClose = () => {
    if (locked || !hasTyped) { onClose(); return; }
    if (!closeKeeping.isPending) closeKeeping.mutate();
  };

  /* PROOF ON A LINE ALREADY SAVED. A claim sent back for proof came back to the rep with its lines
     intact and nowhere to attach the missing bill — the sheet listed them as text only, so the one
     thing the approver asked for could not be done. Uploading here patches that expense's bill. */
  const attachProof = useMutation({
    mutationFn: ({ itemId, billUrl }: { itemId: string; billUrl: string }) =>
      request({ url: `/expense-reports/${report.id}/items/${itemId}`, method: 'PATCH', data: { billUrl } }),
    onSuccess: () => {
      message.success('Proof attached');
      qc.invalidateQueries({ queryKey: ['day-claim', date] });
      qc.invalidateQueries({ queryKey: ['expense-report', String(report?.id)] });
    },
    onError: (e: any) => message.error(e?.response?.data?.error?.message ?? 'Could not attach the proof'),
  });
  /*
   * EDIT / REMOVE A SAVED EXPENSE while the claim is still the rep's (draft, or sent back). A typo in
   * the kilometres used to be permanent short of deleting the whole claim. The server keeps the same
   * rules as adding: travel by the km is re-priced from the rate card (the amount is never typed), the
   * daily allowance is never edited, and an approver's check on a line is cleared if its cost changes.
   */
  type Edit = { id: string; perKm: boolean; actualTravel: boolean; travelMode?: string; distanceKm?: number; category?: string;
    amount?: number; description?: string; visitId?: string; sessionId?: string; billUrl?: string };
  const [editing, setEditing] = useState<Edit | null>(null);
  const startEdit = (i: any) => {
    const perKm = i.distanceKm != null && !!i.travelMode;
    const actualTravel = !perKm && rates.some((r) => r.code === i.category && r.basis === 'ACTUAL');
    setEditing({
      id: String(i.id), perKm, actualTravel, travelMode: i.travelMode ?? undefined,
      distanceKm: i.distanceKm != null ? Number(i.distanceKm) : undefined, category: i.category,
      amount: Number(i.amount), description: i.description ?? '', visitId: i.visitId ? String(i.visitId) : undefined,
      sessionId: i.sessionId ? String(i.sessionId) : undefined, billUrl: i.billUrl ?? undefined,
    });
  };
  const saveEdit = useMutation({
    mutationFn: async (e: Edit) => {
      const orig = saved.items.find((x: any) => String(x.id) === e.id);
      if (!orig) throw new Error('That expense is no longer on the claim');
      const body: Record<string, any> = {};
      if (e.perKm) {
        if (!e.distanceKm || e.distanceKm <= 0) throw new Error('Enter the kilometres travelled');
        if (e.travelMode !== orig.travelMode) body.travelMode = e.travelMode;
        if (Number(e.distanceKm) !== Number(orig.distanceKm)) body.distanceKm = Number(e.distanceKm);
      } else {
        if (!e.amount || e.amount <= 0) throw new Error('Every expense needs an amount');
        if (!e.actualTravel && e.category && e.category !== orig.category) body.category = e.category;
        if (Number(e.amount) !== Number(orig.amount)) body.amount = Number(e.amount);
        const cat = body.category ?? orig.category;
        if (DESC_REQUIRED.has(cat) && !(e.description ?? '').trim()) throw new Error(`${categoryLabel(cat)} expenses need a description`);
      }
      if ((e.description ?? '') !== (orig.description ?? '')) body.description = (e.description ?? '').trim() || null;
      if ((e.visitId ?? null) !== (orig.visitId ? String(orig.visitId) : null)) body.visitId = e.visitId ?? null;
      if ((e.sessionId ?? null) !== (orig.sessionId ? String(orig.sessionId) : null)) body.sessionId = e.sessionId ?? null;
      if ((e.billUrl ?? null) !== (orig.billUrl ?? null)) body.billUrl = e.billUrl ?? null;
      if (!Object.keys(body).length) return false;
      await request({ url: `/expense-reports/${report.id}/items/${e.id}`, method: 'PATCH', data: body });
      return true;
    },
    onSuccess: (changed) => {
      if (changed) message.success('Expense updated');
      setEditing(null);
      qc.invalidateQueries({ queryKey: ['day-claim', date] });
      qc.invalidateQueries({ queryKey: ['expense-report', String(report?.id)] });
      qc.invalidateQueries({ queryKey: ['expense-reports'] });
    },
    onError: (e: any) => message.error(errText(e, 'Could not save the change')),
  });
  const removeLine = useConfirmedMutation((i: any) => ({
    title: 'Remove this expense?',
    content: `${categoryLabel(i.category)}${i.distanceKm > 0 ? ` · ${Number(i.distanceKm)} km` : ''} · ${inr2(i.amount)} comes off this claim.`,
    okText: 'Yes, remove',
    danger: true,
  }), {
    mutationFn: (i: any) => request({ url: `/expense-reports/${report.id}/items/${i.id}`, method: 'DELETE' }),
    onSuccess: () => {
      message.success('Expense removed');
      setEditing(null);
      qc.invalidateQueries({ queryKey: ['day-claim', date] });
      qc.invalidateQueries({ queryKey: ['expense-report', String(report?.id)] });
      qc.invalidateQueries({ queryKey: ['expense-reports'] });
    },
    onError: (e: any) => message.error(errText(e, 'Could not remove it')),
  });

  // Saved lines still missing required proof — the server refuses to submit while any remain.
  const missingProof = (saved.items as any[]).filter((i) => i.proofRequired && !i.billUrl);

  /*
   * NO SUBMIT UNTIL THE BILLS ARE ON. An expense whose policy requires proof (a proofed travel mode, or
   * anything paid for) must carry its bill before the claim can go — so the button is not offered at all
   * until it does, and the footer names the bill still missing. Expenses whose proof is optional never
   * hold it back. Covers both the lines being typed and the ones already on the claim.
   */
  const typedMissingBill = drafts.filter(isTouched).filter((d) => {
    const code = d.kind === 'TRAVEL' ? d.travelMode : d.category;
    return !!code && !d.billUrl && proofRule(code, rates).required;
  });
  const billBlock: string | null = (() => {
    const first = typedMissingBill[0];
    if (first) {
      const code = first.kind === 'TRAVEL' ? first.travelMode : first.category;
      const what = first.kind === 'TRAVEL' ? (rateOf(first.travelMode)?.label ?? 'travel') : categoryLabel(first.category).toLowerCase();
      const more = typedMissingBill.length > 1 ? ` (and ${typedMissingBill.length - 1} more)` : '';
      return `Attach the ${proofRule(code, rates).label.toLowerCase()} for the ${what} expense${more} to submit`;
    }
    if (missingProof.length) {
      return `Attach the missing proof on ${missingProof.length === 1 ? '1 saved expense' : `${missingProof.length} saved expenses`} to submit`;
    }
    return null;
  })();

  const submit = useConfirmedMutation({
    title: 'Submit this expense claim?',
    content: 'Everything on this sheet — including lines you have just typed — goes to your manager for approval, and you can no longer edit it.',
    okText: 'Yes, submit',
  }, {
    mutationFn: async () => {
      await saveTyped();
      // A day's first lines open its claim, so the id may only exist now.
      const id = report?.id ?? (await request<any>({ url: `/expense-reports/day/${date}` }))?.report?.id;
      if (!id) throw new Error('Add a travel or a cost entry first');
      return request({ url: `/expense-reports/${id}/submit`, method: 'PATCH' });
    },
    onSuccess: () => {
      message.success('Claim submitted for approval');
      qc.invalidateQueries({ queryKey: ['day-claim', date] });
      qc.invalidateQueries({ queryKey: ['expense-reports'] });
      onSaved?.();
      onClose();
    },
    onError: (e: any) => message.error(errText(e, 'Could not submit')),
  });

  return (
    <Modal
      open onOpenChange={(o) => { if (!o) requestClose(); }}
      title={`Expenses for ${dayjs(date).format('ddd, DD MMM YYYY')}`}
      description={report
        ? `${report.reportNo} · ${String(report.status).replace(/_/g, ' ')}`
        : others.length ? 'A new claim opens when you add an expense' : 'No claim opened for this day yet'}
      width={860}
      footer={(
        <>
          <span className="mr-auto text-sm text-muted">
            Day so far <span className="font-semibold text-content">{inr2(saved.total + pending.total + othersTotal)}</span>
            {dayKm > 0 && <> · {dayKm} km</>}
          </span>
          <Button variant="default" loading={closeKeeping.isPending} onClick={requestClose}>Close</Button>
          {/* Offered while there is something to send: the open claim, or lines just typed. */}
          {/* Shown, but DISABLED until every required bill is attached — the note beside it says which. */}
          {!locked && (report || hasTyped) && billBlock && (
            <span className="max-w-xs text-right text-sm font-medium text-warning-600 dark:text-warning-500">{billBlock}</span>
          )}
          {!locked && (report || hasTyped) && (
            <Tooltip label={billBlock ?? 'Send this claim to your approver'}>
              <Button variant="primary" icon={<Send size={16} />} loading={submit.isPending} disabled={!!billBlock} onClick={() => submit.mutate()}>
                Submit claim
              </Button>
            </Tooltip>
          )}
        </>
      )}
    >
      {isLoading ? <div className="py-10 text-center"><Spinner /></div> : (
        <div className="space-y-5">
          {/* The day's claims already sent: shown so the same cost is not claimed twice. New lines go to
              a separate claim — a submitted day no longer has to be sent back to take a late expense. */}
          {others.length > 0 && (
            <section>
              <h3 className="mb-2 border-b border-line pb-2 type-body-strong text-content">Already sent for this day</h3>
              <div className="divide-y divide-line/70">
                {others.map((o) => (
                  <div key={o.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-sm">
                    <a href={`/expense-reports/${o.id}`} className="whitespace-nowrap font-medium text-brand-700 hover:underline dark:text-brand-400">{o.reportNo}</a>
                    <Tag tone={o.status === 'SUBMITTED' ? 'blue' : o.status === 'CORRECTION_REQUIRED' ? 'gold' : 'green'}>
                      {String(o.status).replace(/_/g, ' ').toLowerCase()}
                    </Tag>
                    {o.distanceKm > 0 && <span className="whitespace-nowrap text-muted">{o.distanceKm} km</span>}
                    <span className="ml-auto font-semibold tabular-nums text-content">{inr2(o.total)}</span>
                  </div>
                ))}
              </div>
              {!report && (
                <p className="mt-1.5 type-caption text-subtle">Anything you add now goes on a new claim for this day.</p>
              )}
            </section>
          )}

          {/* Sent back: say why, right where the rep can act on it. */}
          {report?.status === 'CORRECTION_REQUIRED' && report.correctionReason && (
            <div className="rounded-card border border-warning-500/40 bg-warning-500/10 px-3 py-2.5 text-sm text-warning-600 dark:text-harvest-300">
              <b>Sent back by your approver:</b> {report.correctionReason}
            </div>
          )}
          {!locked && missingProof.length > 0 && (
            <div className="rounded-card border border-danger-500/40 bg-danger-500/10 px-3 py-2.5 text-sm text-danger-600 dark:text-danger-500">
              {missingProof.length} {missingProof.length === 1 ? 'expense needs' : 'expenses need'} proof before this day can be submitted — attach it below.
            </div>
          )}

          {/* ── What the day already holds ── */}
          {saved.items.length > 0 && (
            <section>
              <h3 className="mb-2 border-b border-line pb-2 type-body-strong text-content">
                {others.length ? `On this claim · ${report?.reportNo ?? ''}` : 'Already on this day'}
              </h3>
              <div className="divide-y divide-line/70">
                {saved.items.map((i: any) => (editing && editing.id === String(i.id) ? (
                  <div key={i.id} className="my-2 rounded-card border border-brand-500/40 bg-surface-2 p-3">
                    <div className="flex flex-wrap items-end gap-3">
                      {editing.perKm ? (
                        <>
                          <label className="min-w-40 flex-1">
                            <span className="mb-1 block text-xs text-muted">How you travelled</span>
                            <Select className="w-full" value={editing.travelMode}
                              onChange={(v) => setEditing({ ...editing, travelMode: v })}
                              options={rates.filter((x) => x.basis === 'PER_KM').map((x) => ({ value: x.code, label: `${x.label} — ${inr2(x.ratePerKm)}/km` }))} />
                          </label>
                          <label className="w-40">
                            <span className="mb-1 block text-xs text-muted">Distance (km)</span>
                            <InputNumber min={0.1} className="w-full" value={editing.distanceKm}
                              onChange={(v) => setEditing({ ...editing, distanceKm: v == null ? undefined : Number(v) })} />
                          </label>
                          {editing.distanceKm ? (
                            <div className="pb-1.5 text-sm">
                              <span className="text-muted">comes to </span>
                              <span className="font-semibold text-content">{inr2(Number(editing.distanceKm) * Number(rateOf(editing.travelMode)?.ratePerKm ?? 0))}</span>
                            </div>
                          ) : null}
                        </>
                      ) : (
                        <>
                          <label className="min-w-40 flex-1">
                            <span className="mb-1 block text-xs text-muted">{editing.actualTravel ? 'How you travelled' : 'What for'}</span>
                            {editing.actualTravel ? (
                              <Input value={rateOf(editing.category)?.label ?? categoryLabel(editing.category)} disabled />
                            ) : (
                              <Select className="w-full" value={editing.category}
                                onChange={(v) => setEditing({ ...editing, category: v })}
                                options={[...new Set([...OTHER_CATEGORIES, String(editing.category)])].map((c) => ({ value: c, label: categoryLabel(c) }))} />
                            )}
                          </label>
                          <label className="w-40">
                            <span className="mb-1 block text-xs text-muted">Amount</span>
                            <InputNumber min={1} prefix="₹" className="w-full" value={editing.amount}
                              onChange={(v) => setEditing({ ...editing, amount: v == null ? undefined : Number(v) })} />
                          </label>
                        </>
                      )}
                    </div>
                    <div className="mt-3 flex flex-wrap items-end gap-3">
                      <label className="min-w-48 flex-1">
                        <span className="mb-1 block text-xs text-muted">
                          Note {!editing.perKm && editing.category && DESC_REQUIRED.has(editing.category) ? '(required)' : '(optional)'}
                        </span>
                        <Input placeholder="What was this for?" value={editing.description ?? ''} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
                      </label>
                      <DayLinkFields visits={dayVisits} shifts={shifts} visitId={editing.visitId} sessionId={editing.sessionId}
                        onChange={(n) => setEditing({ ...editing, ...n })} />
                      <div>
                        <span className="mb-1 block text-xs text-muted">{i.proofRequired ? `${i.proofLabel ?? 'Proof'} (required)` : 'Proof (optional)'}</span>
                        <FileUploadField size="small" category="receipt" buttonLabel={editing.billUrl ? 'Replace' : 'Attach'}
                          value={editing.billUrl} onChange={(url) => setEditing({ ...editing, billUrl: url || undefined })} />
                      </div>
                    </div>
                    {i.verificationStatus && i.verificationStatus !== 'PENDING' && (
                      <p className="mt-2 type-caption text-subtle">Your approver already checked this line — changing what it costs sends it back to them to check again.</p>
                    )}
                    <div className="mt-3 flex justify-end gap-2">
                      <Button size="sm" variant="default" icon={<X size={14} />} onClick={() => setEditing(null)}>Cancel</Button>
                      <Button size="sm" variant="primary" icon={<Check size={14} />} loading={saveEdit.isPending} onClick={() => saveEdit.mutate(editing)}>Save</Button>
                    </div>
                  </div>
                ) : (
                  /* On a phone the text takes the whole width and the proof / amount / edit / remove controls
                     wrap to a row of their own — squeezed into one row, the line broke a word per line and the
                     bill thumbnail sat on top of the kilometres. */
                  <div key={i.id} className="flex flex-wrap items-center gap-x-3 gap-y-2 py-2 text-sm">
                    <span className="min-w-0 basis-full sm:basis-0 sm:flex-1">
                      <span className="font-medium text-content">{categoryLabel(i.category)}</span>
                      {i.travelMode && <span className="text-muted"> · {i.travelMode}</span>}
                      {i.distanceKm > 0 && <span className="text-muted"> · {Number(i.distanceKm)} km @ {inr2(i.ratePerKm)}/km</span>}
                      {i.description && <span className="block truncate text-xs text-subtle">{i.description}</span>}
                      {/* Where in the day it belongs — the shift (only on a day that has several) and the visit. */}
                      {(multiShift || i.visitLabel) && (
                        <span className="mt-0.5 flex flex-wrap items-center gap-1.5 type-caption text-subtle">
                          {multiShift && !i.isSystem && (i.shiftNo
                            ? <ShiftChip no={i.shiftNo} count={shifts.length} />
                            : <span className="rounded-full border border-line px-2 py-0.5">Whole day</span>)}
                          {i.visitLabel && <span className="truncate">Visit: {i.visitLabel}</span>}
                        </span>
                      )}
                    </span>
                    <span className="ml-auto flex shrink-0 flex-wrap items-center justify-end gap-2">
                    {/* isSystem is a 0/1 tinyint — coerce so a non-system line (0) doesn't render a stray "0". */}
                    {!!i.isSystem && <Tag tone="blue">Worked out for you</Tag>}
                    {/* The proof: a small preview, or — while the claim is still editable — the place to add it. */}
                    <span className="flex shrink-0 items-center gap-2">
                      {i.billUrl && <AttachmentCell files={[i.billUrl]} label={`${i.proofLabel ?? 'Proof'} · ${categoryLabel(i.category).toLowerCase()}`} size={32} />}
                      {!i.billUrl && i.proofRequired && (
                        <span className="rounded-full border border-danger-500/40 bg-danger-500/10 px-2 py-0.5 type-caption font-medium text-danger-600 dark:text-danger-500">
                          {i.proofLabel ?? 'Proof'} missing
                        </span>
                      )}
                      {!locked && !i.isSystem && (
                        <FileUploadField size="small" category="receipt" preview={false} compact
                          buttonLabel={i.billUrl ? 'Replace' : `Attach ${String(i.proofLabel ?? 'proof').toLowerCase()}`}
                          value={i.billUrl} onChange={(url) => { if (url) attachProof.mutate({ itemId: String(i.id), billUrl: url }); }} />
                      )}
                    </span>
                    <span className="shrink-0 font-semibold tabular-nums text-content">{inr2(i.amount)}</span>
                    {/* Edit / remove — only while the claim is still the rep's, and never the worked-out allowance. */}
                    {!locked && !i.isSystem && (
                      <span className="flex shrink-0 items-center">
                        <Tooltip label="Edit this expense">
                          <Button variant="text" size="sm" aria-label="Edit expense" disabled={!!editing}
                            icon={<Pencil size={15} />} onClick={() => startEdit(i)} />
                        </Tooltip>
                        <Tooltip label="Remove this expense">
                          <Button variant="text" size="sm" aria-label="Remove saved expense" disabled={!!editing}
                            className="text-muted hover:bg-danger-500/10 hover:text-danger-600"
                            icon={<Trash2 size={15} />} onClick={() => removeLine.mutate(i)} />
                        </Tooltip>
                      </span>
                    )}
                    </span>
                  </div>
                )))}
              </div>
            </section>
          )}

          {/* ── New lines ── */}
          {!locked && (
            <section>
              <div className="mb-2 flex flex-wrap items-center gap-2 border-b border-line pb-2">
                <h3 className="type-body-strong text-content">Add to the day</h3>
                <div className="ml-auto flex flex-wrap gap-2">
                  <Button size="sm" variant="default" icon={<Plus size={14} />} onClick={() => setDrafts((a) => [...a, blank('TRAVEL')])}>Travel</Button>
                  <Button size="sm" variant="default" icon={<Plus size={14} />} onClick={() => setDrafts((a) => [...a, blank('OTHER')])}>Other cost</Button>
                </div>
              </div>

              {drafts.length === 0 ? <EmptyState title="Nothing to add yet" /> : (
                <div className="space-y-3">
                  {drafts.map((d) => {
                    const r = d.kind === 'TRAVEL' ? rateOf(d.travelMode) : undefined;
                    const perKm = r?.basis === 'PER_KM';
                    return (
                      <div key={d.key} className="rounded-card border border-line bg-surface-2 p-3">
                        <div className="flex flex-wrap items-end gap-3">
                          {d.kind === 'TRAVEL' ? (
                            <>
                              <label className="min-w-40 flex-1">
                                <span className="mb-1 block text-xs text-muted">How you travelled</span>
                                <Select
                                  className="w-full" placeholder="Travel mode" value={d.travelMode}
                                  onChange={(v) => patch(d.key, { travelMode: v, distanceKm: undefined, amount: undefined })}
                                  options={rates.map((x) => {
                                    const m = TRAVEL_ICON[String(x.code).toUpperCase()] ?? TRAVEL_ICON.OTHER;
                                    const how = x.basis === 'PER_KM' ? `${inr2(x.ratePerKm)}/km` : 'at actuals';
                                    return {
                                      value: x.code,
                                      /* Plain text for type-to-search; the rendered label carries the icon. */
                                      title: `${x.label} — ${how}`,
                                      label: (
                                        <span className="flex min-w-0 items-center gap-2">
                                          <span className={`shrink-0 ${m.tint}`}>{m.icon}</span>
                                          <span className="truncate text-content">{x.label}</span>
                                          <span className="ml-auto shrink-0 type-caption text-subtle">{how}</span>
                                        </span>
                                      ),
                                    };
                                  })}
                                />
                              </label>
                              {perKm ? (
                                <label className="w-40">
                                  <span className="mb-1 block text-xs text-muted">Distance (km)</span>
                                  <InputNumber min={0.1} className="w-full" placeholder="0"
                                    value={d.distanceKm} onChange={(v) => patch(d.key, { distanceKm: v == null ? undefined : Number(v) })} />
                                </label>
                              ) : d.travelMode ? (
                                <label className="w-40">
                                  <span className="mb-1 block text-xs text-muted">What you spent</span>
                                  <InputNumber min={1} prefix="₹" className="w-full" placeholder="0.00"
                                    value={d.amount} onChange={(v) => patch(d.key, { amount: v == null ? undefined : Number(v) })} />
                                </label>
                              ) : null}
                              {perKm && d.distanceKm ? (
                                <div className="pb-1.5 text-sm">
                                  <span className="text-muted">comes to </span>
                                  <span className="font-semibold text-content">{inr2(Number(d.distanceKm) * Number(r!.ratePerKm))}</span>
                                </div>
                              ) : null}
                            </>
                          ) : (
                            <>
                              <label className="min-w-40 flex-1">
                                <span className="mb-1 block text-xs text-muted">What for</span>
                                <Select className="w-full" placeholder="Category" value={d.category}
                                  onChange={(v) => patch(d.key, { category: v })}
                                  options={OTHER_CATEGORIES.map((c) => ({ value: c, label: categoryLabel(c) }))} />
                              </label>
                              <label className="w-40">
                                <span className="mb-1 block text-xs text-muted">Amount</span>
                                <InputNumber min={1} prefix="₹" className="w-full" placeholder="0.00"
                                  value={d.amount} onChange={(v) => patch(d.key, { amount: v == null ? undefined : Number(v) })} />
                              </label>
                            </>
                          )}
                          <Button variant="text" size="sm" aria-label="Remove expense" className="mb-0.5 text-muted hover:bg-danger-500/10 hover:text-danger-600"
                            icon={<Trash2 size={16} />} onClick={() => drop(d.key)} />
                        </div>

                        <div className="mt-3 flex flex-wrap items-end gap-3">
                          <label className="min-w-48 flex-1">
                            <span className="mb-1 block text-xs text-muted">
                              Note {d.kind === 'OTHER' && d.category && DESC_REQUIRED.has(d.category) ? '(required)' : '(optional)'}
                            </span>
                            <Input placeholder="What was this for?" value={d.description ?? ''} onChange={(e) => patch(d.key, { description: e.target.value })} />
                          </label>
                          <DayLinkFields visits={dayVisits} shifts={shifts} visitId={d.visitId} sessionId={d.sessionId}
                            onChange={(n) => patch(d.key, n)} />
                          <div>
                            <span className="mb-1 block text-xs text-muted">
                              {(() => {
                                const need = d.kind === 'TRAVEL'
                                  ? { required: !!r?.proofRequired, label: r?.proofLabel ?? 'Proof' }
                                  : otherProof(d.category);
                                return need.required ? `${need.label} (required)` : 'Proof (optional)';
                              })()}
                            </span>
                            {/* The bill itself, not a generic image icon: the rep should see WHICH photo they attached. */}
                            <FileUploadField size="small" category="receipt"
                              buttonLabel={d.billUrl ? 'Replace' : 'Attach'} value={d.billUrl} onChange={(url) => patch(d.key, { billUrl: url })} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          )}

          {/* ── The allowance, and why it is what it is ── */}
          <div className="flex flex-wrap items-center gap-2 rounded-card border border-line bg-surface-2 px-3 py-2.5 text-sm">
            <Info size={16} className="shrink-0 text-muted" />
            {daPreview > 0 ? (
              <span className="text-content">
                At <b>{dayKm} km</b> this day earns a daily allowance of <b>{inr2(daPreview)}</b>.
              </span>
            ) : (
              <span className="text-muted">
                {othersDa > 0
                  ? `The day’s daily allowance (${inr2(othersDa)}) is already on an earlier claim — it is paid once a day.`
                  : dayKm > 0
                    ? `${dayKm} km does not reach the distance a daily allowance starts at.`
                    : 'Add your travel and the daily allowance will be worked out from the day’s distance.'}
              </span>
            )}
            <span className="text-subtle">Added automatically when you submit — you don’t enter it.</span>
          </div>
        </div>
      )}
    </Modal>
  );
}
